import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type React from 'react'
import { Fragment } from 'react'
import { Controller, useForm } from 'react-hook-form'
import styled from 'styled-components'

import Button from '@/components/Button'
import type { OutcomeSection, Outcome } from '@/lib/apollo/model/outcome'
import { OutcomeStatus } from '@/lib/apollo/model/outcome'
import { subjectIsInFinalState } from '@/utils/subjectHelpers'

import TextAreaInput from '../input/TextAreaInput'
import Loading from '../Loading'
import type { OptionsConfig } from '../OverrideOutcomeStatusFieldRow/OverrideOutcomeStatusFieldRowDropdown'
import OverrideOutcomeStatusFieldRowDropdown from '../OverrideOutcomeStatusFieldRow/OverrideOutcomeStatusFieldRowDropdown'
import OverrideOutcomeStatusFieldRowToggle from '../OverrideOutcomeStatusFieldRow/OverrideOutcomeStatusFieldRowToggle'

export const OverrideHeader = styled.h3`
  border-top: 2px solid #f2f2f2;
  padding-top: 20px;
`

export interface OverrideOutcomeFieldConfig<T extends OutcomeSection> {
  label: React.ReactNode
  getStatus: (outcomeSection: T) => OutcomeStatus | undefined
  applyOutcomeStatus: (outcomeSection: T, outcomeStatus: OutcomeStatus) => T
  options?: OptionsConfig[]
}

interface Props<T extends OutcomeSection> {
  fieldConfig: OverrideOutcomeFieldConfig<T>[]
  outcomeSection: T
  onChangeOutcomeSection: (outcomeSection: T) => void
  handleSubmit: (reason: string) => void
  isError: boolean
  isLoading: boolean
  isSuccess: boolean
  subjectOutcome?: Outcome
}

export const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr min-content min-content;
  column-gap: 10px;
  margin-bottom: 10px;
`

const LoadingWrapper = styled.div`
  max-width: 100px;
`

const ApplyOverridesButton = styled(Button)`
  margin: 0px;
`

const OverrideOutcomeFields = <T extends OutcomeSection>({
  outcomeSection,
  onChangeOutcomeSection,
  handleSubmit,
  fieldConfig,
  isError,
  isLoading,
  isSuccess,
  subjectOutcome,
}: Props<T>) => {
  const {
    control,
    handleSubmit: handleFormSubmit,
    formState: { errors },
  } = useForm<{ reason: string }>({ defaultValues: { reason: '' } })
  const isMissingReason = errors.reason !== undefined
  const shouldBeDisabled =
    subjectIsInFinalState(subjectOutcome) || outcomeSection.status === OutcomeStatus.pass
  const section = outcomeSection.name
  return (
    <>
      {shouldBeDisabled && (
        <p>
          An applicant cannot be reviewed after the final decision has been made on them.
          This is because the agent will have access to the final report. If this is a
          mistake please open a dev ticket.
        </p>
      )}
      {fieldConfig.map(({ label, getStatus, applyOutcomeStatus, options }, i) => (
        <Fragment key={i}>
          {options && (
            <OverrideOutcomeStatusFieldRowDropdown
              label={label}
              outcomeStatus={getStatus(outcomeSection)}
              onSetOutcome={outcomeStatus => {
                onChangeOutcomeSection(applyOutcomeStatus(outcomeSection, outcomeStatus))
              }}
              options={options}
            />
          )}
          {!options && (
            <OverrideOutcomeStatusFieldRowToggle
              label={label}
              outcomeStatus={getStatus(outcomeSection)}
              onSetOutcome={outcomeStatus => {
                onChangeOutcomeSection(applyOutcomeStatus(outcomeSection, outcomeStatus))
              }}
            />
          )}
        </Fragment>
      ))}
      {isError && (
        <div>
          <p>An error occurred.</p>
        </div>
      )}
      {isLoading && (
        <div>
          <LoadingWrapper>
            <Loading />
          </LoadingWrapper>
        </div>
      )}
      {isSuccess && (
        <div>
          <p>
            Overrides were successfully applied. Please allow a few minutes for the
            platform to show your change.
          </p>
        </div>
      )}
      <form onSubmit={handleFormSubmit(({ reason }) => handleSubmit(reason.trim()))}>
        <OverrideHeader id="override-header">Override {section} data</OverrideHeader>
        <Controller
          control={control}
          name="reason"
          rules={{ required: true, validate: val => val.trim().length > 0 }}
          render={({ field: { onChange, value } }) => (
            <TextAreaInput
              label="Reason for override"
              value={value}
              onChange={onChange}
              aria-labelledby="override-header"
            />
          )}
        />
        {isMissingReason && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              color: '#d32f2f',
              margin: '10px 0px',
            }}
          >
            <FontAwesomeIcon size="1x" icon={faExclamationCircle} />
            <p style={{ margin: '0px' }}>
              Please tell us why you need to apply these overrides.
            </p>
          </div>
        )}
        <div
          className="side-by-side-flex-end"
          style={{ marginTop: isMissingReason ? '0px' : '15px' }}
        >
          <ApplyOverridesButton type="submit" disabled={isLoading || shouldBeDisabled}>
            Apply overrides
          </ApplyOverridesButton>
        </div>
      </form>
    </>
  )
}

export default OverrideOutcomeFields
